import { Box, Button, Group, Stack, Text, Title } from '@mantine/core';
import { useKiosk } from '@/providers/kioskState';
import { useCheckoutByCode, useEquipmentDetail, formatProblemError } from '@commander/api-client';

export function CheckoutConfirmScreen() {
  const { state, showSuccess, showInfo, reset } = useKiosk();
  const checkoutMutation = useCheckoutByCode(state.kioskCode);
  const { data: equipment } = useEquipmentDetail(state.equipmentId ?? '');

  const handleConfirm = async () => {
    if (!state.equipmentId || !state.userId) return;

    try {
      const result = await checkoutMutation.mutateAsync({
        equipmentId: state.equipmentId,
        userId: state.userId,
      });

      const returnMessage = result.expectedReturnAt
        ? `Return by ${new Date(result.expectedReturnAt).toLocaleString()}`
        : '';
      showSuccess(
        `Equipment ${result.equipment.tagId} checked out successfully. ${returnMessage}`.trim()
      );
    } catch (error) {
      showInfo(
        formatProblemError(error, 'Failed to check out equipment'),
        'error',
      );
    }
  };

  return (
    <Stack align="center" gap={36}>
      <Box
        className="kiosk-glow"
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--cmd-accent) 0%, #2563EB 60%, #1D4ED8 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px var(--cmd-accent-glow), 0 2px 8px rgba(0, 0, 0, 0.10)',
        }}
      >
        <svg
          width="44"
          height="44"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12H3M21 12l-7-7M21 12l-7 7" />
        </svg>
      </Box>

      <Stack align="center" gap={4}>
        <Title
          order={2}
          ta="center"
          fw={700}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-primary)',
            fontSize: '1.5rem',
            letterSpacing: '-0.01em',
          }}
        >
          Confirm Check-out
        </Title>
        <Text
          ta="center"
          fw={500}
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: '0.9375rem',
          }}
        >
          Please verify the details below
        </Text>
      </Stack>

      <Box
        style={{
          width: '100%',
          maxWidth: 500,
          borderRadius: 'var(--radius-xl)',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-colour)',
          padding: 'var(--space-8)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <Stack gap="xl">
          <Box>
            <Group justify="space-between" mb={8}>
              <Text
                fw={600}
                style={{
                  fontFamily: 'var(--font-heading)',
                  color: 'var(--text-muted)',
                  fontSize: '0.6875rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Equipment
              </Text>
              <Box
                style={{
                  padding: '3px 10px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--blue-bg)',
                  border: '1px solid rgba(59, 130, 246, 0.20)',
                }}
              >
                <Text
                  fw={700}
                  style={{
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--cmd-accent)',
                    fontSize: '0.625rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Check Out
                </Text>
              </Box>
            </Group>
            <Text
              fw={600}
              style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-primary)',
                fontSize: '1.25rem',
                letterSpacing: '0.04em',
              }}
            >
              {equipment?.tagId ?? state.equipmentId}
            </Text>
            {equipment?.description && (
              <Text
                mt={4}
                fw={500}
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                }}
              >
                {equipment.description}
              </Text>
            )}
          </Box>

          <Box
            style={{
              height: 1,
              background: 'var(--border-colour)',
            }}
          />

          <Box>
            <Text
              fw={600}
              mb={8}
              style={{
                fontFamily: 'var(--font-heading)',
                color: 'var(--text-muted)',
                fontSize: '0.6875rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Employee Badge
            </Text>
            <Text
              fw={600}
              style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-primary)',
                fontSize: '1.25rem',
                letterSpacing: '0.04em',
              }}
            >
              {state.userId}
            </Text>
          </Box>
        </Stack>
      </Box>

      <Group gap="md" wrap="wrap" style={{ width: '100%', maxWidth: 500 }}>
        <Button
          variant="default"
          size="xl"
          h={72}
          flex={1}
          onClick={reset}
          disabled={checkoutMutation.isPending}
          styles={{
            root: {
              fontSize: '1.0625rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-lg)',
              fontFamily: 'var(--font-heading)',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              backgroundColor: 'transparent',
              borderWidth: 1,
              borderColor: 'var(--border-colour)',
              color: 'var(--text-secondary)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          size="xl"
          h={72}
          flex={1}
          onClick={handleConfirm}
          loading={checkoutMutation.isPending}
          styles={{
            root: {
              fontSize: '1.0625rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-lg)',
              fontFamily: 'var(--font-heading)',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              backgroundColor: 'var(--cmd-accent)',
              boxShadow: '0 4px 16px var(--cmd-accent-glow)',
              border: 'none',
              color: 'white',
            },
          }}
        >
          Confirm
        </Button>
      </Group>
    </Stack>
  );
}
